import React, { useCallback, useMemo } from "react";
import { HeaderGroup, flexRender } from "@tanstack/react-table";
import { useEnigmaToken } from "@hooks/useEnigmaToken";
import {
    headerContent,
    headerIcon,
    headerLabel,
    StyledHeaderCell,
    StyledHeaderRow,
    StyledTableHead,
} from "../../table.styles";
import { Direction, IColumnMeta, SortingState } from "../../table.types";
import SortIcon from "./sortIcon";
interface ITableHeaderProps<T> {
    headers: HeaderGroup<T>[];
    stickyHeader?: boolean;
    handleSort: (columnIndex: number) => void;
    sortingState?: SortingState;
    isLoading?: boolean;
}
function TableHeader<T>({
    headers,
    stickyHeader,
    handleSort,
    sortingState,
    isLoading,
}: Readonly<ITableHeaderProps<T>>) {
    const token = useEnigmaToken();

    const actionColumns = useMemo(() => {
        return headers.map((header) => {
            return header.headers.filter((columnHead) => {
                const columnMeta = columnHead.column.columnDef
                    ?.meta as IColumnMeta<T>;
                return columnMeta?.isActionColumn;
            });
        });
    }, [headers]);

    const renderActionColumns = useCallback(() => {
        return (
            <colgroup>
                {actionColumns.map((columnHeads) =>
                    columnHeads.map((columnHead) => {
                        const columnMeta = columnHead.column.columnDef
                            ?.meta as IColumnMeta<T>;
                        return (
                            <col
                                className={columnMeta?.className ?? ""}
                                key={columnHead.id}
                            />
                        );
                    })
                )}
            </colgroup>
        );
    }, [actionColumns]);

    return (
        <>
            {actionColumns.length > 0 && renderActionColumns()}
            <StyledTableHead token={token} stickyHeader={stickyHeader}>
                {headers.map((headerGroup) => (
                    <StyledHeaderRow token={token} key={headerGroup.id}>
                        {headerGroup.headers.map((header) => {
                            const {
                                id,
                                getContext,
                                column: { columnDef },
                            } = header;
                            const {
                                attributes,
                                ellipsis,
                                align = "left",
                                className,
                                sortable,
                            } = columnDef.meta as IColumnMeta<T>;

                            const sortingEnabled =
                                sortingState?.columnIndex === header.index;

                            const direction = sortingEnabled
                                ? sortingState.direction
                                : Direction.ASC;

                            return (
                                <StyledHeaderCell
                                    token={token}
                                    {...(attributes ?? {})}
                                    className={className}
                                    key={id}
                                    textAlign={align}
                                    truncate={ellipsis}
                                >
                                    <div className={headerContent}>
                                        <div className={headerLabel}>
                                            {flexRender(
                                                columnDef.header,
                                                getContext()
                                            )}
                                        </div>

                                        {!isLoading &&
                                            sortingState &&
                                            sortable && (
                                                <SortIcon
                                                    direction={direction}
                                                    enabled={sortingEnabled}
                                                    handleSort={() => {
                                                        handleSort(
                                                            header.index
                                                        );
                                                    }}
                                                    className={headerIcon}
                                                />
                                            )}
                                    </div>
                                </StyledHeaderCell>
                            );
                        })}
                    </StyledHeaderRow>
                ))}
            </StyledTableHead>
        </>
    );
}

export default TableHeader;
